import React, { useState, useEffect, useRef } from "react";
import Select from 'react-select';
import { useAuth } from "../../App";
import { formatDateVN } from "../../utils/formatDate";
import "./myClasses.css";

function getLocalDateYYYYMMDD(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function MyClasses() {
  const { user } = useAuth();
  const apiUrl = (process.env.REACT_APP_API_URL);
  const [classes, setClasses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedGradeForMod, setSelectedGradeForMod] = useState(null);
  const [expandedGradeId, setExpandedGradeId] = useState(null);
  const [expandedClassId, setExpandedClassId] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [error, setError] = useState(null);
  const [successText, setSuccessText] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNewWeekForm, setShowNewWeekForm] = useState(false);
  const [newWeekDate, setNewWeekDate] = useState(getLocalDateYYYYMMDD());
  const [newWeekNumber, setNewWeekNumber] = useState('');
  const datesRef = useRef(null);

  // Ensure we always present 35 week slots to the UI. The server may return
  // a subset; normalize into an array of length 35 where missing weeks have
  // empty records and null date so the teacher can click and take attendance.
  function normalizeTo35Weeks(rawWeeks) {
    const map = new Map();
    if (Array.isArray(rawWeeks)) {
      for (const w of rawWeeks) {
        try {
          const wk = w && (w.week !== undefined && w.week !== null) ? Number(w.week) : null;
          if (wk && wk >= 1 && wk <= 35) {
            map.set(wk, w);
          }
        } catch (e) { /* ignore malformed */ }
      }
    }
    const out = [];
    for (let i = 1; i <= 35; i++) {
      if (map.has(i)) {
        const found = map.get(i) || {};
        const records = Array.isArray(found.records) ? found.records : (Array.isArray(found.students) ? found.students : []);
        out.push({ week: i, date: found.date || null, records });
      } else {
        out.push({ week: i, date: null, records: [] });
      }
    }
    return out;
  }

  useEffect(() => {
    let mounted = true;
    async function load() {
      console.debug('MyClasses: load start', { user, apiUrl });
      if (!apiUrl) return setError('API not configured');
      const token = localStorage.getItem('token');
      if (!token) return setError('You must to be logged in to view this page');
      try {
        const [cres, gres] = await Promise.all([
          fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiUrl}/api/grades`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (!cres.ok) throw new Error('Failed to load classes');
        const data = await cres.json();
        let list = data.classes || [];
        console.debug('MyClasses: classes fetched', { count: (list && list.length) || 0, sample: (list || []).slice(0, 5) });
        if (gres.ok) {
          const gd = await gres.json();
          if (mounted) setGrades(gd.grades || []);
        }
        if (user.role && user.role.toLowerCase() === 'teacher') list = list.filter(c => c.teacher_username === user.username);
        if (user.role && user.role.toLowerCase() === 'mod' && user.school_id) list = list.filter(c => c.school_id === user.school_id);
        if (mounted) setClasses(list);
        // if mod and we have classes, preselect the first grade seen
        if (mounted && user && user.role && user.role.toLowerCase() === 'mod' && (!selectedGradeForMod) && list && list.length) {
          const firstGrade = list.find(c => c.grade_id !== undefined && c.grade_id !== null);
          if (firstGrade) setSelectedGradeForMod(Number(firstGrade.grade_id));
        }
        if (mounted && list.length && user.role && user.role.toLowerCase() === 'teacher') selectClass(list[0]);
      } catch (e) { console.error(e); setError('Cannot loading classes'); }
    }
    load();
    return () => mounted = false;
  }, [apiUrl, user]);

  // Small wrapper around react-select to keep using scalar values
  function RSSelect({ options = [], value, onChange, placeholder = '', isClearable = false, emptyValue = '' }) {
    const opts = options.map(o => (o && typeof o === 'object' && o.value !== undefined) ? o : { value: o, label: String(o) });
    const selected = opts.find(o => String(o.value) === String(value)) || null;
    const menuPortalTarget = (typeof document !== 'undefined') ? document.body : undefined;
    const OPTION_HEIGHT = 28;
    const VISIBLE_OPTIONS = 6;
    const MENU_MAX_HEIGHT = OPTION_HEIGHT * VISIBLE_OPTIONS;
    const compactStyles = {
      control: base => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderRadius: 6, padding: '0 6px', width: 180, maxWidth: '100%' }),
      valueContainer: base => ({ ...base, padding: '0 6px', height: 32 }),
      indicatorsContainer: base => ({ ...base, height: 32 }),
      input: base => ({ ...base, margin: 0, padding: 0 }),
      singleValue: base => ({ ...base, margin: 0 }),
      placeholder: base => ({ ...base, margin: 0, color: '#666', fontSize: 13 }),
      menuPortal: base => ({ ...base, zIndex: 9999 }),
      menu: base => ({ ...base, minWidth: 180, width: 180 }),
      menuList: base => ({ ...base, maxHeight: MENU_MAX_HEIGHT, overflowY: 'auto' }),
      option: base => ({ ...base, fontSize: 13, padding: '4px 8px', minHeight: OPTION_HEIGHT, display: 'flex', alignItems: 'center' })
    };
    return (
      <Select
        options={opts}
        value={selected}
        onChange={opt => onChange(opt ? opt.value : emptyValue)}
        isClearable={isClearable}
        placeholder={placeholder}
        menuPortalTarget={menuPortalTarget}
        menuPosition={'fixed'}
        menuMaxHeight={MENU_MAX_HEIGHT}
        styles={compactStyles}
        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
        classNamePrefix="rs"
        isSearchable={false}
      />
    );
  }

  async function selectClass(cls) {
    setSelectedClass(null);
    setWeeks([]);
    setSelectedWeek(null);
    setStudents([]);
    setAttendanceMap({});
    setError(null);
    // if mod, ensure the grade is selected/highlighted
    if (user && user.role && user.role.toLowerCase() === 'mod' && cls && cls.grade_id) {
      setSelectedGradeForMod(Number(cls.grade_id));
    }
    if (!apiUrl) { setSelectedClass(cls); return; }
    const token = localStorage.getItem('token');
    try {
      const r1 = await fetch(`${apiUrl}/api/classes/${cls.id}/students`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r1.ok) throw new Error('Failed to load students');
      const d1 = await r1.json();
      const studs = d1.students || [];
      console.debug('MyClasses: students response', { classId: cls.id, count: studs.length, sample: studs.slice(0, 5) });
      setSelectedClass(cls);
      setStudents(studs);
      // prefer weekly structured endpoint; if it returns an empty array, fall back to legacy dates
      const r2 = await fetch(`${apiUrl}/api/classes/${cls.id}/attendance/weeks`, { headers: { Authorization: `Bearer ${token}` } });
      if (r2.ok) {
        const d2 = await r2.json();
        // if server returned a school_id, and our selected class doesn't have it, populate it so saves include it
        if (d2 && d2.school_id && (!cls.school_id && cls.school_id !== 0)) {
          setSelectedClass(prev => ({ ...(prev || {}), school_id: d2.school_id }));
        }
        if (Array.isArray(d2.weeks) && d2.weeks.length > 0) {
          setWeeks(normalizeTo35Weeks(d2.weeks || []));
        } else {
          // fallback to legacy dates endpoint when no weeks present
          const r3 = await fetch(`${apiUrl}/api/classes/${cls.id}/attendance/dates`, { headers: { Authorization: `Bearer ${token}` } });
          if (r3.ok) { const d3 = await r3.json(); const dates = d3.dates || []; const mapped = dates.map((dt, i) => ({ week: i + 1, date: dt, records: [] })); setWeeks(normalizeTo35Weeks(mapped)); }
          else setWeeks(normalizeTo35Weeks([]));
        }
      } else {
        const r3 = await fetch(`${apiUrl}/api/classes/${cls.id}/attendance/dates`, { headers: { Authorization: `Bearer ${token}` } });
        if (r3.ok) { const d3 = await r3.json(); const dates = d3.dates || []; const mapped = dates.map((dt, i) => ({ week: i + 1, date: dt, records: [] })); setWeeks(normalizeTo35Weeks(mapped)); }
        else setWeeks(normalizeTo35Weeks([]));
      }
    } catch (e) { console.error(e); setError('Cannot loading class data'); setSelectedClass(cls); }
  }

  useEffect(() => {
    console.debug('MyClasses: selection changed', { selectedClass, studentsCount: (students || []).length });
  }, [selectedClass, students]);

  async function loadWeekDetail(weekObj) {
    // accept optional class object to avoid relying on selectedClass state immediately after selectClass
    let classToUse = selectedClass;
    if (arguments && arguments.length >= 2 && arguments[1]) classToUse = arguments[1];
    if (!classToUse) return;
    setSelectedWeek(null);
    setAttendanceMap({});
    setError(null);
    const token = localStorage.getItem('token');
    // If the weekObj has a records array (could be empty) handle differently
    if (weekObj && Array.isArray(weekObj.records)) {
      if (weekObj.records.length) {
        const map = {};
        (students || []).forEach(s => map[s.id] = { status: 'absent', note: '' });
        weekObj.records.forEach(r => { map[r.student_id] = { status: r.status || 'present', note: r.note || '' }; });
        setAttendanceMap(map);
        setSelectedWeek(weekObj);
        return;
      } else {
        // empty records array: teacher should be able to take attendance (editable);
        // mod should see a "no attendance" message instead of an empty absent list
        const role = (user && user.role || '').toLowerCase();
        if (role === 'teacher') {
          const map = {};
          (students || []).forEach(s => map[s.id] = { status: 'absent', note: '' });
          setAttendanceMap(map);
          setSelectedWeek(weekObj);
          return;
        } else {
          // mark as explicitly no attendance so UI can show the proper message
          setAttendanceMap({});
          setSelectedWeek({ week: weekObj.week, date: weekObj.date || null, records: null, noAttendance: true });
          return;
        }
      }
    }
    try {
      const date = weekObj.date;
      const res = await fetch(`${apiUrl}/api/classes/${classToUse.id}/attendance/${date}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('No detail');
      const data = await res.json();
      const map = {};
      (students || []).forEach(s => map[s.id] = { status: 'absent', note: '' });
      (data.students || []).forEach(r => { map[r.id] = { status: r.status || 'present', note: r.note || '' }; });
      setAttendanceMap(map);
      setSelectedWeek({ week: weekObj.week, date: date, records: data.students || [] });
    } catch (e) {
      const map = {};
      (students || []).forEach(s => map[s.id] = { status: 'absent', note: '' });
      // If fetching detail fails, decide based on role: teacher sees editable empty list,
      // mod sees 'no attendance' indicator.
      setAttendanceMap(map);
      const role = (user && user.role || '').toLowerCase();
      if (role === 'teacher') {
        setSelectedWeek({ week: weekObj.week, date: weekObj.date, records: [] });
      } else {
        setSelectedWeek({ week: weekObj.week, date: weekObj.date, records: null, noAttendance: true });
      }
    }
  }

  function togglePresent(id) { setAttendanceMap(prev => ({ ...prev, [id]: { ...(prev[id] || {}), status: prev[id] && prev[id].status === 'present' ? 'absent' : 'present' } })); }
  function setNote(id, note) { setAttendanceMap(prev => ({ ...prev, [id]: { ...(prev[id] || {}), note } })); }

  async function saveWeekAttendance() {
    if (!selectedClass || !selectedWeek) return setError('No week selected');
    const token = localStorage.getItem('token');
    const studentsPayload = Object.keys(attendanceMap).map(k => ({ id: Number(k), status: attendanceMap[k].status, note: attendanceMap[k].note }));
    // Ensure we always send a date to the server. If selectedWeek.date is missing (teacher created an empty week),
    // fall back to the newWeekDate state (if teacher used the new-week form) or today's local date.
    const fallbackDate = newWeekDate || getLocalDateYYYYMMDD();
    const dateToSend = selectedWeek && selectedWeek.date ? selectedWeek.date : fallbackDate;
    const payload = { week: selectedWeek.week, date: dateToSend, students: studentsPayload, school_id: selectedClass && selectedClass.school_id ? selectedClass.school_id : null };
    try {
      setSaving(true);
      console.log('Saving attendance payload:', payload);
      const res = await fetch(`${apiUrl}/api/classes/${selectedClass.id}/attendance`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      let body = null;
      try { body = await res.json(); } catch (e) { body = null; }
      if (!res.ok) {
        console.error('Save failed', res.status, body);
        const msg = (body && body.message) ? body.message : (`Save failed (status ${res.status})`);
        setError(msg);
        setSaving(false);
        return;
      }
      // success - server may return { weeks } or { dates } depending on flow
      // refresh weeks from server (prefer weeks endpoint)
      try {
        const rW = await fetch(`${apiUrl}/api/classes/${selectedClass.id}/attendance/weeks`, { headers: { Authorization: `Bearer ${token}` } });
        if (rW.ok) { const wbody = await rW.json(); if (Array.isArray(wbody.weeks)) setWeeks(normalizeTo35Weeks(wbody.weeks)); }
        else {
          const rD = await fetch(`${apiUrl}/api/classes/${selectedClass.id}/attendance/dates`, { headers: { Authorization: `Bearer ${token}` } });
          if (rD.ok) { const db = await rD.json(); setWeeks(normalizeTo35Weeks((db.dates || []).map((dt, i) => ({ week: i + 1, date: dt, records: [] })))); }
        }
      } catch (e) { /* ignore refresh errors */ }

      // refresh weeks and select the saved week briefly, then clear selection so teacher can create next week
      try {
        const rW2 = await fetch(`${apiUrl}/api/classes/${selectedClass.id}/attendance/weeks`, { headers: { Authorization: `Bearer ${token}` } });
        let refreshed = null;
        if (rW2.ok) { const wb = await rW2.json(); if (Array.isArray(wb.weeks)) { setWeeks(normalizeTo35Weeks(wb.weeks)); refreshed = wb.weeks; } }
        if (!refreshed) { const rD2 = await fetch(`${apiUrl}/api/classes/${selectedClass.id}/attendance/dates`, { headers: { Authorization: `Bearer ${token}` } }); if (rD2.ok) { const db2 = await rD2.json(); const mapped = (db2.dates || []).map((dt, i) => ({ week: i + 1, date: dt, records: [] })); setWeeks(normalizeTo35Weeks(mapped)); refreshed = mapped; } }
        const found = (refreshed || weeks || []).find(w => Number(w.week) === Number(payload.week) || w.date === payload.date);
        if (found) {
          // show saved week details
          await loadWeekDetail(found);
          // then reset selection so teacher can create a new week (per requirement)
          setTimeout(() => { setSelectedWeek(null); setAttendanceMap({}); }, 800);
        }
      } catch (e) { }
      setSuccessText('Saved attendance');
      setTimeout(() => setSuccessText(''), 4000);
      setError(null);
      setSaving(false);
    } catch (e) { console.error('Save error', e); setError('Cannot saving attendance (network or server error)'); }
  }

  // helper: resolve avatar urls similar to Home.jsx normalizeUserUrl
  function resolveAvatarUrl(src) {
    const raw = src || '';
    const s = String(raw || '');
    if (!s) return (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg');
    if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('//')) return s;
    if (s.startsWith('/')) {
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      return base + s;
    }
    // relative path like 'uploads/avt/foo.jpg'
    const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
    return base ? (base + '/' + s.replace(/^\//, '')) : s;
  }

  return (
    <div className="classes-page page-view">
      <main className="class-main">
        <div className="year-text">Year 2025-2026</div>
        <div className="class-view">
          <div className="left-column">
            {user && user.role && user.role.toLowerCase() === 'mod' && (
              <div className="mod-filters card">
                <div className="filters-row">
                  <div className="filter-item">
                    <label>Grades</label>
                    <div style={{ width: 120 }}>
                      <RSSelect
                        options={[{ value: '', label: 'Select grade' }].concat((grades || []).map(g => ({ value: g.id, label: g.name })))}
                        value={selectedGradeForMod || ''}
                        onChange={v => { const vv = v ? Number(v) : null; setSelectedGradeForMod(vv); setSelectedClass(null); setWeeks([]); setSelectedWeek(null); setStudents([]); setExpandedGradeId(null); setExpandedClassId(null); }}
                        emptyValue={''}
                        isClearable={true}
                        placeholder="Select grade"
                      />
                    </div>
                  </div>
                  <div className="filter-item">
                    <label>Classes</label>
                    <div style={{ width: 140 }}>
                      <RSSelect
                        options={[{ value: '', label: selectedGradeForMod ? 'Select class' : 'Select grade first' }].concat(((classes || []).filter(c => selectedGradeForMod && Number(c.grade_id) === Number(selectedGradeForMod))).map(c => ({ value: c.id, label: c.name })))}
                        value={selectedClass ? (selectedClass.id || '') : ''}
                        onChange={v => { const id = v ? Number(v) : null; const cls = (classes || []).find(c => Number(c.id) === id) || null; if (cls) selectClass(cls); else { setSelectedClass(null); setWeeks([]); setStudents([]); } }}
                        emptyValue={''}
                        isClearable={true}
                        isDisabled={!selectedGradeForMod}
                        placeholder={selectedGradeForMod ? 'Select class' : 'Select grade first'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {user.role && user.role.toLowerCase() === 'teacher' ? (
              <div className="card">
                <h4>Weeks</h4>
                <div className="vstack">
                  <div className="weeks-list">
                    {(weeks || []).map((w, idx) => {
                      const key = `${w.date || ''}-${w.week !== undefined && w.week !== null ? w.week : 'legacy'}-${idx}`;
                      const label = (w && w.week) ? `Week ${w.week}` : (w && w.date ? w.date : `Week ${idx + 1}`);
                      return (
                        <button key={key} className={`date ${selectedWeek && selectedWeek.week === w.week && selectedWeek.date === w.date ? 'active' : ''}`} onClick={() => { setSelectedWeek(w); loadWeekDetail(w); }}>
                          {label}
                          <div className="sub">{w.date || ''}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              // non-teacher:
              // - For 'mod' role we no longer render the left-side grade/class nested list here (filters moved to top).
              //   Instead show the weeks card so weeks sit on the left as requested.
              // - For other roles show the class list as before.
              (user.role && user.role.toLowerCase() === 'mod') ? (
                <div className="card">
                  <h4>Weeks</h4>
                  <div className="vstack">
                    {/* For mod: only show weeks when a class is selected */}
                    {selectedClass ? (
                      <div className="weeks-list">
                        {(weeks || []).map((w, idx) => {
                          const key = `${w.date || ''}-${w.week !== undefined && w.week !== null ? w.week : 'legacy'}-${idx}`;
                          const label = (w && w.week) ? `Week ${w.week}` : (w && w.date ? w.date : `Week ${idx + 1}`);
                          return (
                            <button key={key} className={`date ${selectedWeek && selectedWeek.week === w.week && selectedWeek.date === w.date ? 'active' : ''}`} onClick={() => { setSelectedWeek(w); loadWeekDetail(w); }}>
                              {label}
                              <div className="sub">{w.date || ''}</div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="muted">Please select a class to view weeks</div>
                    )}
                    {/* Note: creation of new weeks is teacher-only; Mod does not get create controls */}
                  </div>
                </div>
              ) : (
                <div className="card left-list-card">
                  <h4>List of Classes</h4>
                  {classes.length === 0 ? <div className="muted">No classes</div> : (
                    <ul className="left-class-list">
                      {classes.map(c => (
                        <li key={c.id} className={`class-item ${selectedClass && selectedClass.id === c.id ? 'active' : ''}`} onClick={() => selectClass(c)}>
                          <div className="class-name">{c.name}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            )}
          </div>

          {/* On desktop the right column shows selected class and attendance; on mobile it will stack under the left column */}
          <div className="right-column">
            {!selectedClass ? (
              <div className="card empty"><div className="muted">Please select a class to view details</div></div>
            ) : (
              <div className="card class-info">
                <div className="class-header">
                  <h2 className="class-title">Class: {selectedClass.name}</h2>
                  <div className="teacher-card">
                    { /* avatar: prefer teacher_avatar or teacher_avt, fallback to a generic path */}
                    <img alt={selectedClass.teacher_full_name || selectedClass.teacher_username || 'Teacher'}
                      className="teacher-avt"
                      src={resolveAvatarUrl(selectedClass.avt || selectedClass.teacher_avt || selectedClass.teacher_avatar || (selectedClass.teacher_username ? `uploads/avt/${selectedClass.teacher_username}.png` : 'uploads/avt/avt-def.jpg'))}
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg'); }}
                    />
                    <div className="teacher-info">
                      <div className="teacher-name">{(selectedClass && (selectedClass.teacher_last_name || selectedClass.teacher_first_name)) ? [selectedClass.teacher_last_name, selectedClass.teacher_first_name].filter(Boolean).join(' ') : (selectedClass.teacher_full_name || selectedClass.teacher_username || selectedClass.teacher)}</div>
                      <div className="teacher-role">{selectedClass.teacher_role || selectedClass.teacher_type || 'Teacher'}</div>
                    </div>
                  </div>
                </div>

                {selectedWeek ? (
                  <div className="week-meta mt-8">
                    <strong>Week:</strong> {selectedWeek.week} &nbsp; <strong>Date:</strong> {selectedWeek.date || 'N/A'}
                  </div>
                ) : (
                  <div className="muted mt-8">Please select a week to view attendance list or perform attendance actions</div>
                )}

                {selectedWeek ? (
                  <>
                    {successText && <div className="success">{successText}</div>}

                    {/* If noAttendance is true or records === null and user is not teacher, show message */}
                    {((selectedWeek.noAttendance || selectedWeek.records === null) && (user && user.role && user.role.toLowerCase() !== 'teacher')) ? (
                      <div className="muted">No attendance list for this week</div>
                    ) : (
                      <>
                        {/* <h4>List of Students</h4> */}
                        {user.role && user.role.toLowerCase() === 'teacher' && selectedWeek && Array.isArray(selectedWeek.records) && selectedWeek.records.length === 0 && (
                          <div className="edit-controls">
                            <button className="btn secondary" onClick={() => {
                              const map = {};
                              (students || []).forEach(s => map[s.id] = { status: 'present', note: (attendanceMap[s.id] && attendanceMap[s.id].note) || '' });
                              setAttendanceMap(map);
                            }}>Select All</button>
                            <button className="btn secondary" onClick={() => {
                              const map = {};
                              (students || []).forEach(s => map[s.id] = { status: 'absent', note: (attendanceMap[s.id] && attendanceMap[s.id].note) || '' });
                              setAttendanceMap(map);
                            }}>Deselect All</button>
                            <button className="btn" onClick={saveWeekAttendance} disabled={saving}>{saving ? 'Saving...' : 'Save Attendance'}</button>
                            {error && <div className="error mt-12">{error}</div>}
                          </div>
                        )}
                        <ul className="students-grid">
                          {(students || []).sort((a, b) => (((a.last_name || a.first_name) ? [a.last_name, a.first_name].filter(Boolean).join(' ') : (a.full_name || a.name || ''))).localeCompare(((b.last_name || b.first_name) ? [b.last_name, b.first_name].filter(Boolean).join(' ') : (b.full_name || b.name || '')))).map(s => {
                            const status = attendanceMap[s.id] && attendanceMap[s.id].status ? attendanceMap[s.id].status : null;
                            const noteVal = attendanceMap[s.id] && attendanceMap[s.id].note ? attendanceMap[s.id].note : '';
                            const isEditable = user.role && user.role.toLowerCase() === 'teacher' && selectedWeek && Array.isArray(selectedWeek.records) && selectedWeek.records.length === 0;
                            return (
                              <li key={s.id} className={status === 'present' ? 'student-present' : (status === 'absent' ? 'student-absent' : '')}>
                                <div className="student-row">
                                  {isEditable ? (
                                    <input type="checkbox" checked={status === 'present'} onChange={() => togglePresent(s.id)} />
                                  ) : (
                                    // display no symbol/text here; color indicates status
                                    null
                                  )}
                                  <div className="student-name">{(s && (s.last_name || s.first_name)) ? [s.last_name, s.first_name].filter(Boolean).join(' ') : (s.full_name || s.username)}</div>
                                  {/* status text removed; row color indicates attendance */}
                                </div>

                                {/* Note input visible only when editing AND status is 'absent' */}
                                {isEditable && status === 'absent' ? (
                                  <div className="note">
                                    <input type="text" placeholder="Reason" value={noteVal} onChange={e => setNote(s.id, e.target.value)} className="note-input" />
                                  </div>
                                ) : (
                                  // read-only note: only show when status is 'absent'
                                  status === 'absent' ? (
                                    <div className="absence-reason">Reason: {noteVal || 'N/A'}</div>
                                  ) : (
                                    <div />
                                  )
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </>
                ) : null}

              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

